// InterCash Account (QOR staff): QOR's own account at InterCash, which every load onto an InterCash card is paid
// from. The balances are asked of InterCash when the page opens (and on Refresh); what was loaded is read from the
// API's own record of the loads InterCash confirmed. Every figure is hidden until its eye is clicked.
import { amount, api, money } from '@qor/web-shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { usePageTitle } from '../components/ConsoleLayout';
import { HiddenNumber, useShownNumbers } from '../components/HiddenNumber';
import { useStaff } from '../components/RequireStaff';
import { easternSeconds } from './ProcessorLog';

interface Balance {
  currency: string;
  balance: number | null;
  lowBalanceThreshold: number | null;
  isLow: boolean;
  message: string | null;
}

interface Account {
  provider: string;
  checkedAt: string;
  balances: Balance[];
}

interface Window {
  amount: number;
  count: number;
}

interface Loads {
  totals: { currency: string; last24Hours: Window; last7Days: Window; last30Days: Window }[];
  recent: {
    at: string;
    amount: number;
    currency: string;
    card: string | null;
    reference: string | null;
    loadedBy: string | null;
  }[];
}

interface Statement {
  configured: boolean;
  message: string | null;
  entityName: string | null;
  balance: number | null;
  from: string;
  to: string;
  summary: { kind: string; currency: string | null; count: number; total: number }[];
  totalCount: number;
  page: number;
  pageSize: number;
  transactions: {
    at: string | null;
    amount: number | null;
    currency: string | null;
    kind: string;
    card: string | null;
  }[];
}

/** What a card's history lookup costs: nine statement lines in ten, left out of the list unless asked for. */
const HISTORY_FEE = 'Card Txn History Fee';
const WITHOUT_HISTORY_FEES = '-history-fees';

const WINDOWS = [
  ['last24Hours', 'Last 24 hours'],
  ['last7Days', 'Last 7 days'],
  ['last30Days', 'Last 30 days'],
] as const;

const amountOf = (value: number, currency: string) => `${money(value, currency)} ${currency}`;

export function InterCashAccountPage() {
  usePageTitle('InterCash Account');
  // the page of one processor: gone, like its menu entry, while another is in view
  const isInterCash = useStaff().scope.activeProvider === 'InterCash';
  const account = useQuery({
    queryKey: ['intercash-account'],
    enabled: isInterCash,
    queryFn: async (): Promise<Account> => {
      const a = await api<{
        provider: string;
        checkedAt: string;
        balances: (Omit<Balance, 'balance' | 'lowBalanceThreshold'> & {
          balance: string | null;
          lowBalanceThreshold: string | null;
        })[];
      }>('GET', '/admin/intercash/account');
      return {
        ...a,
        balances: a.balances.map((b) => ({
          ...b,
          balance: amount(b.balance),
          lowBalanceThreshold: amount(b.lowBalanceThreshold),
        })),
      };
    },
  });
  const loads = useQuery({
    queryKey: ['intercash-account-loads'],
    enabled: isInterCash,
    queryFn: async (): Promise<Loads> => {
      type Raw = { amount: string; count: number };
      const l = await api<{
        totals: { currency: string; last24Hours: Raw; last7Days: Raw; last30Days: Raw }[];
        recent: (Omit<Loads['recent'][number], 'amount'> & { amount: string })[];
      }>('GET', '/admin/intercash/loads');
      const w = (x: Raw) => ({ amount: Number(x.amount), count: x.count });
      return {
        totals: l.totals.map((t) => ({
          currency: t.currency,
          last24Hours: w(t.last24Hours),
          last7Days: w(t.last7Days),
          last30Days: w(t.last30Days),
        })),
        recent: l.recent.map((r) => ({ ...r, amount: Number(r.amount) })),
      };
    },
  });
  const numbers = useShownNumbers();
  const balances = account.data?.balances ?? [];
  const totals = loads.data?.totals ?? [];
  const recent = loads.data?.recent ?? [];
  const labels = [
    ...balances.map((b) => `${b.currency} balance`),
    ...totals.flatMap((t) => WINDOWS.map(([, name]) => `${t.currency} loaded, ${name.toLowerCase()}`)),
    ...recent.map((_, i) => `amount of load ${i + 1}`),
  ];
  const low = balances.filter((b) => b.isLow);
  const [refreshed, setRefreshed] = useState(0);
  const refreshing = account.isFetching || loads.isFetching;
  if (!isInterCash) return <Navigate to="/dashboard" replace />;

  return (
    <>
      <div className="qc-page-heading">
        <h1>InterCash Account</h1>
        <p className="qc-breadcrumb">
          QOR Console <span aria-hidden="true">&rsaquo;</span> <strong>InterCash Account</strong>
        </p>
      </div>
      <p className="qc-breadcrumb" style={{ marginBottom: 12 }}>
        QOR&rsquo;s own account at InterCash: every load onto an InterCash card is paid from it. Figures are hidden
        until you click the eye beside them.
      </p>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button
          type="button"
          className="qor-button qor-button-primary"
          disabled={refreshing}
          onClick={() => {
            void account.refetch();
            void loads.refetch();
            setRefreshed((n) => n + 1);
          }}
        >
          Refresh
        </button>
        <button
          type="button"
          className="qc-button-ghost"
          onClick={() => (numbers.anyShown ? numbers.hideAll() : numbers.showAll(labels))}
        >
          {numbers.anyShown ? 'Hide all figures' : 'Show all figures'}
        </button>
      </div>

      {low.length > 0 && (
        <p className="qc-auth-error" role="alert">
          Low balance: {low.map((b) => b.currency).join(', ')}. A load is refused when the account cannot pay for it.
        </p>
      )}

      <div className="qc-panel qc-account-panel" aria-label="Account balances" role="region">
        <div className="qc-panel-title">
          <h3>Account balance</h3>
          {account.data && <small>As InterCash reported at {easternSeconds(account.data.checkedAt)}</small>}
        </div>
        {account.isError ? (
          <p className="qc-empty">The balances could not be read. Try Refresh.</p>
        ) : account.isPending ? (
          <p className="qc-empty">Asking InterCash&hellip;</p>
        ) : (
          <div className="qc-table-scroll">
            <table className="qc-plain-table">
              <thead>
                <tr>
                  <th>Currency</th>
                  <th>Balance</th>
                  <th>Warn below</th>
                  <th>State</th>
                </tr>
              </thead>
              <tbody>
                {balances.map((b) => (
                  <tr key={b.currency}>
                    <td>{b.currency}</td>
                    <td className={b.isLow ? 'qc-balance-low' : undefined}>
                      {b.balance === null ? (
                        '--'
                      ) : (
                        <HiddenNumber
                          value={amountOf(b.balance, b.currency)}
                          {...numbers.props(`${b.currency} balance`)}
                        />
                      )}
                    </td>
                    <td>{b.lowBalanceThreshold === null ? 'Not set' : amountOf(b.lowBalanceThreshold, b.currency)}</td>
                    <td>
                      {b.balance === null ? (
                        <span className="qc-badge qc-badge-warning">{b.message ?? 'Not available'}</span>
                      ) : b.isLow ? (
                        <span className="qc-badge qc-badge-danger">Low</span>
                      ) : (
                        <span className="qc-badge qc-badge-success">OK</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <StatementPanel refreshed={refreshed} />

      <div className="qc-panel qc-account-panel" aria-label="Loaded onto cards" role="region">
        <div className="qc-panel-title">
          <h3>Loaded onto cards</h3>
          <small>
            The loads InterCash accepted, from the{' '}
            <Link to="/processor-log?operation=CardLoadRequestC">processor log</Link>
          </small>
        </div>
        {loads.isError ? (
          <p className="qc-empty">The loads could not be read. Try Refresh.</p>
        ) : loads.isPending ? (
          <p className="qc-empty">Loading&hellip;</p>
        ) : totals.length === 0 ? (
          <p className="qc-empty">No load in the last 30 days.</p>
        ) : (
          <div className="qc-table-scroll">
            <table className="qc-plain-table">
              <thead>
                <tr>
                  <th>Currency</th>
                  {WINDOWS.map(([, name]) => (
                    <th key={name}>{name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {totals.map((t) => (
                  <tr key={t.currency}>
                    <td>{t.currency}</td>
                    {WINDOWS.map(([key, name]) => (
                      <td key={key}>
                        <HiddenNumber
                          value={`${amountOf(t[key].amount, t.currency)} in ${t[key].count} load${t[key].count === 1 ? '' : 's'}`}
                          {...numbers.props(`${t.currency} loaded, ${name.toLowerCase()}`)}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="qc-panel qc-account-panel" aria-label="Recent loads" role="region">
        <div className="qc-panel-title">
          <h3>Recent loads</h3>
          <small>The latest {recent.length || ''} of the last 30 days, newest first; times are US Eastern</small>
        </div>
        {recent.length === 0 ? (
          <p className="qc-empty">{loads.isPending ? 'Loading…' : 'No load in the last 30 days.'}</p>
        ) : (
          <div className="qc-table-scroll">
            <table className="qc-plain-table">
              <thead>
                <tr>
                  <th>When (Eastern)</th>
                  <th>Amount</th>
                  <th>Card ID</th>
                  <th>Loaded by</th>
                  <th>InterCash reference</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((load, i) => (
                  <tr key={`${load.at}-${load.reference ?? i}`}>
                    <td style={{ whiteSpace: 'nowrap' }}>{easternSeconds(load.at)}</td>
                    <td>
                      <HiddenNumber
                        value={amountOf(load.amount, load.currency)}
                        {...numbers.props(`amount of load ${i + 1}`)}
                      />
                    </td>
                    <td>{load.card === null ? '--' : `•••• ${load.card}`}</td>
                    <td>{load.loadedBy ?? '--'}</td>
                    <td>{load.reference ?? '--'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

/**
 * The statement of the account as InterCash keeps it (GetTransHistorySubAbl): every amount paid out of it, fees
 * included, between two days (UTC). Totals per kind of line, then the lines, newest first.
 */
function StatementPanel({ refreshed }: { refreshed: number }) {
  const today = new Date().toISOString().slice(0, 10);
  const [filter, setFilter] = useState({ from: today, to: today, kind: WITHOUT_HISTORY_FEES, page: 1 });
  const numbers = useShownNumbers();
  const statement = useQuery({
    queryKey: ['intercash-account-statement', filter, refreshed],
    placeholderData: keepPreviousData,
    queryFn: () => {
      const query = new URLSearchParams({ from: filter.from, to: filter.to, page: String(filter.page) });
      if (filter.kind === WITHOUT_HISTORY_FEES) query.set('withoutKind', HISTORY_FEE);
      else if (filter.kind !== '') query.set('kind', filter.kind);
      return api<
        Omit<Statement, 'balance' | 'summary' | 'transactions'> & {
          balance: string | null;
          summary: (Omit<Statement['summary'][number], 'total'> & { total: string })[];
          transactions: (Omit<Statement['transactions'][number], 'amount'> & { amount: string | null })[];
        }
      >('GET', `/admin/intercash/account/statement?${query.toString()}`).then((s): Statement => ({
        ...s,
        balance: amount(s.balance),
        summary: s.summary.map((x) => ({ ...x, total: Number(x.total) })),
        transactions: s.transactions.map((t) => ({ ...t, amount: amount(t.amount) })),
      }));
    },
  });
  const data = statement.data;
  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const text = (name: string) => {
      const value = form.get(name);
      return typeof value === 'string' ? value : '';
    };
    numbers.hideAll();
    setFilter({ from: text('From') || today, to: text('To') || text('From') || today, kind: text('Kind'), page: 1 });
  };
  const pages = data ? Math.max(1, Math.ceil(data.totalCount / data.pageSize)) : 1;
  const amountText = (value: number | null, currency: string | null) =>
    value === null ? '--' : `${money(value, currency)} ${currency ?? ''}`.trim();
  const turn = (page: number) => {
    numbers.hideAll();
    setFilter((now) => ({ ...now, page }));
  };

  return (
    <div className="qc-panel qc-account-panel" aria-label="Transaction history" role="region">
      <div className="qc-panel-title">
        <h3>Transaction history</h3>
        <small>
          The statement of the account, as InterCash keeps it{data?.entityName ? ` (${data.entityName})` : ''}: what was
          paid out of it, fees included. Days are UTC
        </small>
      </div>
      <form className="qc-cardholder-filters" onSubmit={submit}>
        <input type="date" name="From" defaultValue={filter.from} aria-label="Statement from" required />
        <input type="date" name="To" defaultValue={filter.to} aria-label="Statement to" required />
        <select name="Kind" aria-label="Kind of line" defaultValue={filter.kind}>
          <option value={WITHOUT_HISTORY_FEES}>All but history fees</option>
          <option value="">All lines</option>
          {[...new Set((data?.summary ?? []).map((s) => s.kind))].sort().map((kind) => (
            <option key={kind} value={kind}>
              {kind}
            </option>
          ))}
        </select>
        <button type="submit" className="qor-button qor-button-primary" disabled={statement.isFetching}>
          Show
        </button>
      </form>

      {statement.isError ? (
        <p className="qc-empty">
          The statement could not be read{statement.error instanceof Error ? `: ${statement.error.message}` : '.'}
        </p>
      ) : !data ? (
        <p className="qc-empty">Asking InterCash&hellip;</p>
      ) : data.message !== null ? (
        <p className="qc-empty">{data.message}</p>
      ) : (
        <>
          <h4 style={{ margin: '14px 0 6px' }}>
            Totals, {data.from === data.to ? data.from : `${data.from} to ${data.to}`}
          </h4>
          {data.summary.length === 0 ? (
            <p className="qc-empty">Nothing on the statement for these days.</p>
          ) : (
            <div className="qc-table-scroll">
              <table className="qc-plain-table">
                <thead>
                  <tr>
                    <th>Kind</th>
                    <th>Lines</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.summary.map((sum) => (
                    <tr key={`${sum.kind}-${sum.currency ?? ''}`}>
                      <td>{sum.kind}</td>
                      <td>{sum.count}</td>
                      <td>
                        <HiddenNumber
                          value={amountText(sum.total, sum.currency)}
                          {...numbers.props(`total of ${sum.kind}${sum.currency ? ` in ${sum.currency}` : ''}`)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h4 style={{ margin: '18px 0 6px' }}>Lines ({data.totalCount}), newest first; times are US Eastern</h4>
          {data.transactions.length === 0 ? (
            <p className="qc-empty">No line of this kind for these days.</p>
          ) : (
            <div className="qc-table-scroll">
              <table className="qc-plain-table">
                <thead>
                  <tr>
                    <th>When (Eastern)</th>
                    <th>Kind</th>
                    <th>Card ID</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {data.transactions.map((line, i) => (
                    <tr key={`${line.at ?? ''}-${i}`}>
                      <td style={{ whiteSpace: 'nowrap' }}>{line.at === null ? '--' : easternSeconds(line.at)}</td>
                      <td>{line.kind}</td>
                      <td>{line.card === null ? '--' : `\u2022\u2022\u2022\u2022 ${line.card}`}</td>
                      <td>
                        <HiddenNumber
                          value={amountText(line.amount, line.currency)}
                          {...numbers.props(`amount of statement line ${i + 1}`)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {pages > 1 && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
              <button
                type="button"
                className="qc-button-ghost"
                disabled={data.page <= 1 || statement.isFetching}
                onClick={() => turn(data.page - 1)}
              >
                Previous
              </button>
              <span>
                Page {data.page} of {pages}
              </span>
              <button
                type="button"
                className="qc-button-ghost"
                disabled={data.page >= pages || statement.isFetching}
                onClick={() => turn(data.page + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
